import { Soundfont, CacheStorage, StopFn } from 'smplr';
import { InstrumentId, VirtualInstrument } from '../types';
import { ModeledSynthPiano } from './ModeledSynthPiano';
import { snapshotDecodeStats, decodingFailedSince } from '../decodeGuard';

/**
 * Acoustic Grand Piano
 * Real acoustic concert grand piano sampled in high-fidelity MP3 format.
 * Sourced from the MusyngKite MIDI.js soundfont library with 100% universal decoding across all browsers.
 * Local IndexedDB/CacheStorage caching via smplr with seamless fallback to modeled synth.
 */
export class AcousticGrandPiano implements VirtualInstrument {
  public readonly id: InstrumentId = 'acoustic-grand';
  public isReady = false;

  private ctx: AudioContext;
  private destination: AudioNode;
  private smplrPiano: ReturnType<typeof Soundfont> | null = null;
  private fallbackSynth: ModeledSynthPiano;
  private loadPromise: Promise<void> | null = null;
  private onProgress?: (percent: number) => void;
  private onStatusChange?: () => void;
  private currentDecayTime = 1.6;

  constructor(
    ctx: AudioContext,
    destination: AudioNode,
    options?: {
      onProgress?: (percent: number) => void;
      onStatusChange?: () => void;
    }
  ) {
    this.ctx = ctx;
    this.destination = destination;
    this.onProgress = options?.onProgress;
    this.onStatusChange = options?.onStatusChange;
    this.fallbackSynth = new ModeledSynthPiano(ctx, destination);
  }

  public async load(): Promise<void> {
    if (this.isReady) return;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = (async () => {
      try {
        const storage = typeof window !== 'undefined' && 'caches' in window ? new CacheStorage() : undefined;
        const decodeStatsBefore = snapshotDecodeStats(this.ctx);
        this.smplrPiano = new Soundfont(this.ctx, {
          instrument: 'acoustic_grand_piano',
          kit: 'MusyngKite',
          instrumentUrl: 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/acoustic_grand_piano-mp3.js',
          destination: this.destination,
          volume: 96,
          storage,
          onLoadProgress: (p) => {
            if (p.total > 0) {
              const pct = Math.min(100, Math.round((p.loaded / p.total) * 100));
              this.onProgress?.(pct);
            }
          },
        });

        await this.smplrPiano.ready;
        if (decodingFailedSince(this.ctx, decodeStatsBefore)) {
          throw new Error('All samples failed to decode in this browser (unsupported codec) — using modeled synth fallback');
        }
        this.isReady = true;
        this.onProgress?.(100);
        this.onStatusChange?.();
      } catch (err) {
        console.warn('[AcousticGrandPiano] Sample loading failed or cancelled; using modeled synth fallback', err);
        this.onStatusChange?.();
      }
    })();

    return this.loadPromise;
  }

  public setDecayTime(decaySeconds: number): void {
    this.currentDecayTime = Math.max(0.05, Math.min(3.5, decaySeconds));
    this.fallbackSynth.setDecayTime?.(this.currentDecayTime);
  }

  public playNoteOn(pitch: number, velocity: number = 90, scheduledTime?: number): (stopTime?: number) => void {
    if (!this.isReady || !this.smplrPiano) {
      if (!this.loadPromise) {
        this.load().catch(() => {});
      }
      return this.fallbackSynth.playNoteOn(pitch, velocity, scheduledTime);
    }

    try {
      const stopFn: StopFn = this.smplrPiano.start({
        note: pitch,
        velocity: Math.max(1, Math.min(127, Math.round(velocity))),
        time: scheduledTime !== undefined ? scheduledTime : this.ctx.currentTime,
        ampRelease: this.currentDecayTime,
      } as Parameters<typeof this.smplrPiano.start>[0]);

      return (stopTime?: number) => {
        try {
          stopFn(stopTime);
        } catch {
          // voice may already be ended
        }
      };
    } catch {
      return this.fallbackSynth.playNoteOn(pitch, velocity, scheduledTime);
    }
  }

  public scheduleNote(pitch: number, velocity: number = 90, startTime: number, durationSeconds: number = 0.5): void {
    if (!this.isReady || !this.smplrPiano) {
      if (!this.loadPromise) {
        this.load().catch(() => {});
      }
      this.fallbackSynth.scheduleNote(pitch, velocity, startTime, durationSeconds);
      return;
    }

    try {
      this.smplrPiano.start({
        note: pitch,
        velocity: Math.max(1, Math.min(127, Math.round(velocity))),
        time: startTime,
        duration: Math.max(0.05, durationSeconds),
        ampRelease: this.currentDecayTime,
      } as Parameters<typeof this.smplrPiano.start>[0]);
    } catch {
      this.fallbackSynth.scheduleNote(pitch, velocity, startTime, durationSeconds);
    }
  }

  public stopAll(): void {
    if (this.smplrPiano) {
      try {
        this.smplrPiano.stop();
      } catch {
        // ignore
      }
    }
    this.fallbackSynth.stopAll();
  }
}
